/**
 * Universal PNG codec that works in both browser and Node.
 * Replaces `pngjs` with `fast-png` (pure JavaScript, zero native dependencies).
 */
import { decode, encode } from 'fast-png';
import type { PixelData } from './types.js';

/**
 * Decode a PNG buffer/Uint8Array into raw RGBA pixel data.
 */
export function decodePng(input: Uint8Array | ArrayBufferLike | Buffer): PixelData {
  // fast-png accepts Uint8Array or ArrayBuffer
  const data = input instanceof Uint8Array ? input : new Uint8Array(input);
  const decoded = decode(data);
  // fast-png returns { width, height, data: Uint8Array | Uint16Array, channels, depth }
  // If channels < 4 (RGB without alpha), expand to RGBA
  let rgba: Uint8Array;
  if (decoded.channels === 3) {
    const src = decoded.data;
    rgba = new Uint8Array(decoded.width * decoded.height * 4);
    const pixelCount = decoded.width * decoded.height;
    for (let i = 0; i < pixelCount; i++) {
      rgba[i * 4] = src[i * 3];
      rgba[i * 4 + 1] = src[i * 3 + 1];
      rgba[i * 4 + 2] = src[i * 3 + 2];
      rgba[i * 4 + 3] = 255;
    }
  } else {
    rgba = decoded.data instanceof Uint8Array ? decoded.data : new Uint8Array(decoded.data.buffer);
  }

  return {
    width: decoded.width,
    height: decoded.height,
    data: rgba,
  };
}

/**
 * Encode raw RGBA pixel data into a PNG Uint8Array.
 */
export function encodePng(pixels: PixelData): Uint8Array {
  return encode({
    width: pixels.width,
    height: pixels.height,
    data: pixels.data instanceof Uint8Array ? pixels.data : new Uint8Array(pixels.data),
    channels: 4,
    depth: 8,
  });
}

/**
 * Copy a rectangular region from one pixel buffer to another (replaces PNG.bitblt).
 */
export function blitPixels(
  src: PixelData,
  dst: PixelData,
  srcX: number,
  srcY: number,
  width: number,
  height: number,
  dstX: number,
  dstY: number,
): void {
  for (let row = 0; row < height; row++) {
    const srcOffset = ((srcY + row) * src.width + srcX) * 4;
    const dstOffset = ((dstY + row) * dst.width + dstX) * 4;
    const srcSlice = src.data instanceof Uint8Array
      ? src.data.subarray(srcOffset, srcOffset + width * 4)
      : new Uint8Array(src.data).subarray(srcOffset, srcOffset + width * 4);
    if (dst.data instanceof Uint8Array) {
      dst.data.set(srcSlice, dstOffset);
    } else {
      const dstArr = new Uint8Array(dst.data);
      dstArr.set(srcSlice, dstOffset);
    }
  }
}

/**
 * Create an empty RGBA pixel buffer.
 */
export function createPixelData(width: number, height: number): PixelData {
  return {
    width,
    height,
    data: new Uint8Array(width * height * 4),
  };
}

/**
 * Crop a rectangular region from source pixel data, returning new PixelData.
 * Combines createPixelData + blitPixels in one call.
 */
export function cropPixels(
  src: PixelData,
  x: number,
  y: number,
  width: number,
  height: number,
): PixelData {
  const dst = createPixelData(width, height);
  blitPixels(src, dst, x, y, width, height, 0, 0);
  return dst;
}
